import React, { useRef, useEffect, useState } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { solarizedlight } from "react-syntax-highlighter/dist/esm/styles/prism";
import { sanitizeHtml } from "../utils/htmlSanitizer";

interface HtmlRendererProps {
  textContent: string;
  viewMode: "code" | "rendered";
  zoom: number;
  selectedTheme: string;
  syntaxThemes: Array<{ label: string; value: string; style: any }>;
  isFullscreen: boolean;
  onError: (error: string) => void;
}

const HtmlRenderer: React.FC<HtmlRendererProps> = ({
  textContent,
  viewMode,
  zoom,
  selectedTheme,
  syntaxThemes,
  isFullscreen,
  onError,
}) => {
  const [sanitizedHtml, setSanitizedHtml] = useState<string>("");
  const htmlIframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (viewMode === "rendered" && textContent) {
      const { sanitized, error } = sanitizeHtml(textContent);
      if (error) {
        onError(error);
        setSanitizedHtml("");
      } else {
        setSanitizedHtml(sanitized);
        onError("");
      }
    }
  }, [textContent, viewMode, onError]);

  if (viewMode === "rendered") {
    return (
      <div style={{
        maxHeight: isFullscreen ? "88vh" : "70vh",
        overflow: "auto",
        padding: "16px",
        background: "#fff",
      }}>
        <iframe
          ref={htmlIframeRef}
          srcDoc={sanitizedHtml}
          sandbox="allow-same-origin allow-scripts"
          style={{
            width: "100%",
            height: "400px",
            border: "1px solid #d9d9d9",
            borderRadius: "4px",
            background: "#fff",
          }}
          onError={() => onError("Iframe rendering failed")}
        />
      </div>
    );
  }

  // Code view for HTML
  return (
    <div style={{ 
      transform: `scale(${zoom})`,      transformOrigin: "top left", 
      transition: "transform 0.2s",
    }}>
      <SyntaxHighlighter
        language="html"
        style={syntaxThemes.find(t => t.value === selectedTheme)?.style || solarizedlight}
        showLineNumbers={true}
        wrapLines={true}
        customStyle={{
          maxHeight: "100%",
          overflow: "auto",
          borderRadius: "4px",
          fontSize: "14px",
        }}
      >
        {textContent}
      </SyntaxHighlighter>
    </div>
  );
};

export default HtmlRenderer;
